import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, FormGroup, Alert } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { DateTimePicker } from 'react-tempusdominus-bootstrap';
import moment from 'moment';

import { useTripRecordsState } from '../../Context';
import Loader from '../Loader';
import { createTripRecord, updateTripRecord } from '../../Context/actions/tripRecordsActions';

const DateTimeField = ({ name, label, format = 'MM/DD/YYYY HH:mm', control, rules, error }) => (
    <FormGroup>
        <Form.Label style={{ display: 'block' }}>
            {label} {rules?.required && <span className="text-danger">*</span>}
        </Form.Label>
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => {
                // Guard against Invalid Dates reaching the picker — passing one throws during
                // render and, with no error boundary, white-screens the whole app (TSM-13766).
                const parsedDate = field.value ? new Date(field.value) : undefined;
                const validDate = parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : undefined;
                return (
                    <label className="test datepicker-wrapper" style={{ display: 'block' }}>
                        <DateTimePicker
                            date={validDate}
                            defaultDate={validDate}
                            format={format}
                            onChange={(date) => {
                                if (date?.date) field.onChange(date.date);
                            }}
                            keepOpen
                        />
                    </label>
                );
            }}
        />
        {error && <span className="text-danger small">{error.message}</span>}
    </FormGroup>
);

const OdometerField = ({ name, label, control }) => (
    <FormGroup>
        <Form.Label>{label}</Form.Label>
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <Form.Control
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="—"
                />
            )}
        />
    </FormGroup>
);

const TripRecord = ({ onCloseClick, dispatchDetails, editItem, refreshTripRecords, loading }) => {
    const { tripRecordsDispatch } = useTripRecordsState();
    const [validationError, setValidationError] = useState('');

    const { handleSubmit, setValue, control } = useForm({
        defaultValues: {
            startTime: null,
            startOdometer: '',
            firstPickupTime: null,
            firstPickupOdometer: '',
            finalDropoffTime: null,
            lastDropoffOdometer: '',
            endTime: null,
            endOdometer: '',
        },
    });

    useEffect(() => {
        if (editItem) {
            const dateTimeFields = ['startTime', 'firstPickupTime', 'finalDropoffTime', 'endTime'];
            Object.keys(editItem).forEach((key) => {
                if (editItem[key] !== null && editItem[key] !== undefined) {
                    if (dateTimeFields.includes(key)) {
                        // The backend sends these fields as 'MM-DD-YYYY HH:mm' strings. Parse with the
                        // explicit format — a bare moment()/new Date() rejects the dashed format on
                        // iOS WebView (WebKit), producing "Invalid Date" (TSM-13766).
                        const parsed = moment(editItem[key], 'MM-DD-YYYY HH:mm');
                        if (parsed.isValid()) {
                            setValue(key, parsed.format('YYYY-MM-DDTHH:mm'));
                        }
                    } else {
                        setValue(key, editItem[key]);
                    }
                }
            });
        }
    }, [editItem, setValue]);

    const onSubmit = async (data) => {
        setValidationError('');

        const hasStartEndTimes = data.startTime && data.endTime;
        const hasStartEndOdos = data.startOdometer && data.endOdometer;

        const hasFirstPickupLastDropoffTimes = data.firstPickupTime && data.finalDropoffTime;

        if (!hasStartEndTimes && !hasStartEndOdos) {
            setValidationError(
                'You must complete either (Start Time AND End Time) OR (Start Odometer AND End Odometer) to save.'
            );
            return;
        }

        if (hasStartEndTimes && moment(data.startTime).isAfter(moment(data.endTime))) {
            setValidationError('Start Time cannot be later than End Time.');
            return;
        }

        if (hasFirstPickupLastDropoffTimes && moment(data.firstPickupTime).isAfter(moment(data.finalDropoffTime))) {
            setValidationError('First Pickup Time cannot be later than Final Dropoff Time.');
            return;
        }

        const odometerFields = [
            { key: 'startOdometer', label: 'Start Odometer' },
            { key: 'firstPickupOdometer', label: 'First Pickup Odometer' },
            { key: 'lastDropoffOdometer', label: 'Last Dropoff Odometer' },
            { key: 'endOdometer', label: 'End Odometer' },
        ].filter((field) => data[field.key] !== '' && data[field.key] !== null && data[field.key] !== undefined);

        for (let i = 0; i < odometerFields.length - 1; i++) {
            const current = odometerFields[i];
            const next = odometerFields[i + 1];
            if (Number(data[current.key]) >= Number(data[next.key])) {
                setValidationError(`${current.label} must be less than ${next.label}.`);
                return;
            }
        }

        try {
            const dateTimeFields = ['startTime', 'firstPickupTime', 'finalDropoffTime', 'endTime'];

            let payload = {
                ...data,
                dispatchID: dispatchDetails.id,
                companyID: dispatchDetails.companyID,
                driverID: dispatchDetails.driverID,
            };

            dateTimeFields.forEach((key) => {
                if (data[key]) {
                    payload[key] = moment(data[key]).format('YYYY-MM-DDTHH:mm:00');
                }
            });

            Object.keys(payload).forEach((key) => {
                if (payload[key] === '') payload[key] = null;
            });

            if (!editItem) {
                await createTripRecord(tripRecordsDispatch, payload);
            } else {
                await updateTripRecord(tripRecordsDispatch, editItem.id, payload);
            }

            await refreshTripRecords();
            onCloseClick();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Modal
            className="rounded warning-modal"
            animation={false}
            show={true}
            size="lg"
            onHide={onCloseClick}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header className="bg-primary text-white">
                <Modal.Title className="mx-2 text-white">{editItem ? 'Edit' : 'Add'} Trip Record</Modal.Title>
                <i
                    className="fas fa-times text-white closeButton"
                    onClick={onCloseClick}
                    style={{ cursor: 'pointer' }}
                />
            </Modal.Header>

            <Form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={loading}>
                    <Modal.Body className="mt-3">
                        {loading && <Loader />}

                        {validationError && (
                            <Alert variant="danger" className="mb-3">
                                {validationError}
                            </Alert>
                        )}

                        <p className="text-muted small mb-3">
                            Complete either <strong>Start &amp; End Time</strong> OR{' '}
                            <strong>Start &amp; End Odometer</strong> to save.
                        </p>

                        <Row>
                            <Col xs={12} md={6}>
                                <DateTimeField name="startTime" label="Start Time" control={control} />
                            </Col>
                            <Col xs={12} md={6}>
                                <OdometerField name="startOdometer" label="Start Odometer" control={control} />
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={6}>
                                <DateTimeField name="firstPickupTime" label="First Pickup Time" control={control} />
                            </Col>
                            <Col xs={12} md={6}>
                                <OdometerField
                                    name="firstPickupOdometer"
                                    label="First Pickup Odometer"
                                    control={control}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={6}>
                                <DateTimeField name="finalDropoffTime" label="Final Dropoff Time" control={control} />
                            </Col>
                            <Col xs={12} md={6}>
                                <OdometerField
                                    name="lastDropoffOdometer"
                                    label="Last Dropoff Odometer"
                                    control={control}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={6}>
                                <DateTimeField name="endTime" label="End Time" control={control} />
                            </Col>
                            <Col xs={12} md={6}>
                                <OdometerField name="endOdometer" label="End Odometer" control={control} />
                            </Col>
                        </Row>
                    </Modal.Body>
                </fieldset>

                <Modal.Footer>
                    <Button variant="danger" onClick={onCloseClick} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="success" type="submit" disabled={loading}>
                        Save
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default TripRecord;
